// Quality API
import api, { type AxiosResponse } from './client';
import type {
  QualityMetric,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  NCRTechnicalReview,
  NCRCollectionProcess,
  NCRCreditProcess,
  InspectionRecord,
  QualityDashboardStats,
  QualityDashboardSummary,
  InspectionPassRate,
  Concession,
  ConcessionDocument,
  ConcessionComment,
  ConcessionActionLog,
  ConcessionDashboardStats,
  SourceOfRejection,
  FaultCodeGroup,
  FaultCodeCategory,
  ApprovalGroup,
  Accreditation,
  QueryParams,
} from './types';

export const qualityAPI = {
  // Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: QualityMetric[] }>> =>
    api.get('/quality/metrics/', { params }),

  getDashboardSummary: (): Promise<AxiosResponse<QualityDashboardSummary>> =>
    api.get('/quality/metrics/dashboard_summary/'),

  // NCRs
  getNCRs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: NonConformanceReport[] }>> =>
    api.get('/quality/ncrs/', { params }),

  getNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.get(`/quality/ncrs/${id}/`),

  createNCR: (data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post('/quality/ncrs/', data),

  updateNCR: (id: number, data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.patch(`/quality/ncrs/${id}/`, data),

  // CR-3396: Accept view context params for hierarchical filtering
  getNCRStats: (params?: Record<string, string | number>): Promise<AxiosResponse<QualityDashboardStats>> =>
    api.get('/quality/ncrs/dashboard_stats/', { params }),

  closeNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/close_ncr/`),

  // NCR Workflow Actions
  submitDetail: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_detail/`),

  submitRCA: (id: number, data: {
    why_1: string;
    why_2: string;
    why_3: string;
    why_4: string;
    why_5: string;
    selected_root_cause: string;
    immediate_corrective_action: string;
    disposition_instructions?: string;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_rca/`, data),

  submitCAR: (id: number, data: {
    corrective_action: string;
    preventive_action: string;
    impact_assessment: string;
    // 8D: Impact on Similar Products
    impact_on_similar_products?: string;
    similar_products_affected?: boolean;
    // 8D: Training
    training_required: boolean;
    training_details?: string;
    // 8D: Documentation Updates
    doc_process_flow_updated?: boolean;
    doc_control_plan_updated?: boolean;
    doc_fmea_updated?: boolean;
    doc_work_instructions_updated?: boolean;
    doc_other_updated?: string;
    documentation_references: string;
    corrective_action_notes: string;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_car/`, data),

  rejectNCR: (id: number, rejection_reason: string): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reject_ncr/`, { rejection_reason }),

  approveRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/approve_rejection/`),

  declineRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/decline_rejection/`),

  reopenNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reopen_ncr/`),

  confirmCollection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-collection/`),

  confirmCredit: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-credit/`),

  // Collection Process - Multi-step workflow
  getCollectionProcess: (ncrId: number): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/collection/`),

  initiateCollection: (ncrId: number, data: {
    proposed_collection_date?: string;
    collection_address?: string;
    collection_notes?: string;
    supplier_notes?: string;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/initiate/`, data),

  updateCollectionStatus: (ncrId: number, data: {
    status: string;
    collection_notes?: string;
    confirmed_collection_date?: string;
    tracking_reference?: string;
    actual_collection_date?: string;
    customer_receipt_notes?: string;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/update-status/`, data),

  // Credit Process - Multi-step workflow
  getCreditProcess: (ncrId: number): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/credit/`),

  initiateCredit: (ncrId: number, data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    supplier_notes?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/initiate/`, data),

  updateCreditStatus: (ncrId: number, data: {
    status: string;
    credit_amount_confirmed?: number;
    supplier_credit_reference?: string;
    approval_notes?: string;
    credit_amount?: number;
    credit_reason?: string;
    customer_notes?: string;
    credit_note_reference?: string;
    credit_debit_number?: string;
    credit_issue_date?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/update-status/`, data),

  // NCR Supporting Resources
  getNCRDocuments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRDocument[] }>> =>
    api.get('/quality/ncr-documents/', { params: { ncr: ncrId } }),

  uploadNCRDocument: (data: FormData): Promise<AxiosResponse<NCRDocument>> =>
    api.post('/quality/ncr-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteNCRDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/ncr-documents/${id}/`),

  getNCRComments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRComment[] }>> =>
    api.get('/quality/ncr-comments/', { params: { ncr: ncrId } }),

  addNCRComment: (ncrId: number, comment: string, is_internal: boolean): Promise<AxiosResponse<NCRComment>> =>
    api.post('/quality/ncr-comments/', { ncr: ncrId, comment, is_internal }),

  getNCRActionLogs: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRActionLog[] }>> =>
    api.get('/quality/ncr-logs/', { params: { ncr: ncrId } }),

  getNCRTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.get('/quality/ncr-technical-reviews/', { params: { ncr: ncrId } }),

  createTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post('/quality/ncr-technical-reviews/', { ncr: ncrId }),

  completeTechnicalReview: (id: number, review_notes: string, review_outcome: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/complete_review/`, { review_notes, review_outcome }),

  rejectTechnicalReview: (id: number, rejection_reason: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/reject_review/`, { rejection_reason }),

  // Inspections
  getInspections: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: InspectionRecord[] }>> =>
    api.get('/quality/inspections/', { params }),

  getPassRate: (): Promise<AxiosResponse<InspectionPassRate>> =>
    api.get('/quality/inspections/pass_rate/'),

  // Concessions (CR-3470)
  getConcessions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Concession[] }>> =>
    api.get('/quality/concessions/', { params }),

  getConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.get(`/quality/concessions/${id}/`),

  createConcession: (data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.post('/quality/concessions/', data),

  updateConcession: (id: number, data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.patch(`/quality/concessions/${id}/`, data),

  deleteConcession: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concessions/${id}/`),

  getConcessionStats: (params?: Record<string, string | number>): Promise<AxiosResponse<ConcessionDashboardStats>> =>
    api.get('/quality/concessions/dashboard_stats/', { params }),

  // Concession Workflow Actions
  submitConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/submit/`),

  markConcessionUnderReview: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/mark_under_review/`),

  approveConcession: (id: number, data: {
    review_notes?: string;
    quantity_approved?: number;
    valid_until?: string;
  }): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/approve/`, data),

  rejectConcession: (id: number, rejection_reason: string): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/reject/`, { rejection_reason }),

  closeConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/close/`),

  acceptConcessionRisk: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/accept_risk/`),

  // Concession Supporting Resources
  getConcessionDocuments: (concessionId: number): Promise<AxiosResponse<ConcessionDocument[]>> =>
    api.get(`/quality/concessions/${concessionId}/documents/`),

  uploadConcessionDocument: (data: FormData): Promise<AxiosResponse<ConcessionDocument>> =>
    api.post('/quality/concession-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteConcessionDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concession-documents/${id}/`),

  getConcessionComments: (concessionId: number): Promise<AxiosResponse<ConcessionComment[]>> =>
    api.get(`/quality/concessions/${concessionId}/comments/`),

  addConcessionComment: (concessionId: number, comment: string, is_internal: boolean): Promise<AxiosResponse<ConcessionComment>> =>
    api.post(`/quality/concessions/${concessionId}/add_comment/`, { comment, is_internal }),

  getConcessionActionLog: (concessionId: number): Promise<AxiosResponse<ConcessionActionLog[]>> =>
    api.get(`/quality/concessions/${concessionId}/action_log/`),

  // Quality Config (admin-managed dropdown options)
  getSourcesOfRejection: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SourceOfRejection[] }>> =>
    api.get('/quality/sources-of-rejection/', { params }),

  createSourceOfRejection: (data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.post('/quality/sources-of-rejection/', data),

  updateSourceOfRejection: (id: number, data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.patch(`/quality/sources-of-rejection/${id}/`, data),

  deleteSourceOfRejection: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/sources-of-rejection/${id}/`),

  getFaultGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeGroup[] }>> =>
    api.get('/quality/fault-groups/', { params }),

  createFaultGroup: (data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.post('/quality/fault-groups/', data),

  updateFaultGroup: (id: number, data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.patch(`/quality/fault-groups/${id}/`, data),

  deleteFaultGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-groups/${id}/`),

  getFaultCategories: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeCategory[] }>> =>
    api.get('/quality/fault-categories/', { params }),

  createFaultCategory: (data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.post('/quality/fault-categories/', data),

  updateFaultCategory: (id: number, data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.patch(`/quality/fault-categories/${id}/`, data),

  deleteFaultCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-categories/${id}/`),

  // CR-5333: Approval Groups + Accreditations.
  getApprovalGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ApprovalGroup[] }>> =>
    api.get('/quality/approval-groups/', { params }),

  createApprovalGroup: (data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.post('/quality/approval-groups/', data),

  updateApprovalGroup: (id: number, data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.patch(`/quality/approval-groups/${id}/`, data),

  deleteApprovalGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/approval-groups/${id}/`),

  getAccreditations: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Accreditation[] }>> =>
    api.get('/quality/accreditations/', { params }),

  createAccreditation: (data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.post('/quality/accreditations/', data),

  updateAccreditation: (id: number, data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.patch(`/quality/accreditations/${id}/`, data),

  deleteAccreditation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/accreditations/${id}/`),
};
